const QUESTION_TYPES_WITH_OPTIONS = new Set([
  "MC",
  "MCM",
  "ACIQ",
  "ACMC",
  "ACIQM",
]);
const SINGLE_ANSWER_TYPES = new Set(["MC", "ACIQ", "ACMC", "CTA"]);
const MULTI_ANSWER_TYPES = new Set(["MCM", "ACIQM"]);
const QUESTION_AUDIO_REQUIRED = new Set(["ACMC", "ACIQ", "CT", "ACIQM", "CTA"]);
const QUESTION_TYPES_REQUIRING_PHRASE = new Set(["UVI", "LAR"]);

const trimValue = (value) => (typeof value === "string" ? value.trim() : "");

const buildQuestionLabel = ({
  unitIndex,
  unitName,
  levelIndex,
  levelName,
  questionIndex,
}) => {
  const unitLabel = `Unit ${unitIndex + 1}${unitName ? ` (${unitName})` : ""}`;
  const levelLabel = `Level ${levelIndex + 1}${levelName ? ` (${levelName})` : ""}`;
  const questionLabel = `Question ${questionIndex + 1}`;
  return `${unitLabel} > ${levelLabel} > ${questionLabel}`;
};

const validateQuestion = (question, context) => {
  const errors = [];
  const label = buildQuestionLabel(context);
  const qt = question?.questionType;
  const text = trimValue(question?.question);
  const answer = trimValue(question?.Answer);
  const hintType = question?.hintType ?? null;
  const hint = trimValue(question?.hint);
  const hintUrl = trimValue(question?.hintUrl);
  const phrase = trimValue(question?.phrase);
  const questionAudioUrl = trimValue(
    question?.url || question?.questionUrl || question?.audioUrl,
  );

  if (!qt) {
    errors.push(`${label}: question type is required.`);
  }
  if (!text) {
    errors.push(`${label}: question text is required.`);
  }

  if (QUESTION_TYPES_WITH_OPTIONS.has(qt)) {
    if (qt === "ACMC") {
      const optionEntries = Object.entries(question?.options || {});
      const filled = optionEntries.filter(
        ([optionText, audio]) => trimValue(optionText) && trimValue(audio),
      );
      if (!filled.length) {
        errors.push(`${label}: add at least one option with audio for ACMC.`);
      }

      const hasAudioForEach = optionEntries.every(
        ([optionText, audio]) => trimValue(optionText) && trimValue(audio),
      );
      if (!hasAudioForEach) {
        errors.push(`${label}: each ACMC option needs text and audio.`);
      }

      if (
        !answer ||
        !optionEntries.some(([optionText]) => optionText === answer)
      ) {
        errors.push(`${label}: answer must match an ACMC option.`);
      }
    } else {
      const optionList = Array.isArray(question?.options)
        ? question.options.map((opt) => trimValue(opt)).filter(Boolean)
        : [];
      if (optionList.length < 2) {
        errors.push(`${label}: provide at least two options.`);
      }

      const answerParts = MULTI_ANSWER_TYPES.has(qt)
        ? answer
            .split(";")
            .map((part) => trimValue(part))
            .filter(Boolean)
        : [answer].filter(Boolean);
      if (!answerParts.length) {
        errors.push(`${label}: select at least one correct option.`);
      } else {
        const missing = answerParts.filter((ans) => !optionList.includes(ans));
        if (missing.length) {
          errors.push(`${label}: answer must match an existing option.`);
        }
        if (SINGLE_ANSWER_TYPES.has(qt) && answerParts.length > 1) {
          errors.push(`${label}: only one option can be correct.`);
        }
      }
    }
  }

  if (QUESTION_AUDIO_REQUIRED.has(qt) && !questionAudioUrl) {
    errors.push(`${label}: question audio is required.`);
  }

  if (QUESTION_TYPES_REQUIRING_PHRASE.has(qt)) {
    if (!phrase) {
      errors.push(`${label}: phrase is required for ${qt}.`);
    }
    if (hintType !== "audio") {
      errors.push(`${label}: hint type must be audio for ${qt}.`);
    }
  }

  if (hintType === "text" && !hint) {
    errors.push(`${label}: hint text is required when hint type is text.`);
  }
  if (hintType === "audio" && !hintUrl) {
    errors.push(`${label}: hint audio is required when hint type is audio.`);
  }

  return errors;
};

export const validateEntireCourseContent = (fullCourseContent) => {
  const courseData = fullCourseContent?.data;
  const units = Array.isArray(courseData?.units) ? courseData.units : [];
  const errors = [];

  if (!units.length) {
    errors.push("Course must include at least one unit.");
    return errors;
  }

  units.forEach((unit, unitIndex) => {
    const unitName = trimValue(unit?.name);
    if (!unitName) {
      errors.push(`Unit ${unitIndex + 1}: name is required.`);
    }
    if (!trimValue(unit?.description)) {
      errors.push(
        `Unit ${unitIndex + 1}${unitName ? ` (${unitName})` : ""}: description is required.`,
      );
    }

    const levels = Array.isArray(unit?.levels) ? unit.levels : [];
    if (!levels.length) {
      errors.push(
        `Unit ${unitIndex + 1}${unitName ? ` (${unitName})` : ""}: add at least one level.`,
      );
      return;
    }

    levels.forEach((level, levelIndex) => {
      const levelName = trimValue(level?.name);
      if (!levelName) {
        errors.push(
          `Unit ${unitIndex + 1}${unitName ? ` (${unitName})` : ""} > Level ${
            levelIndex + 1
          }: name is required.`,
        );
      }

      const questions = Array.isArray(level?.questions) ? level.questions : [];
      if (!questions.length) {
        errors.push(
          `Unit ${unitIndex + 1}${unitName ? ` (${unitName})` : ""} > Level ${
            levelIndex + 1
          }${levelName ? ` (${levelName})` : ""}: add at least one question.`,
        );
        return;
      }

      questions.forEach((question, questionIndex) => {
        errors.push(
          ...validateQuestion(question, {
            unitIndex,
            unitName,
            levelIndex,
            levelName,
            questionIndex,
          }),
        );
      });
    });
  });

  return errors;
};

export const validateUnitContent = (levelContent, unitName, unitOrder) => {
  const errors = [];
  const levelName = trimValue(levelContent?.name);
  if (!levelName) {
    errors.push("Level name is required.");
  }

  // Check for questions array and validate each question if present
  const questions = Array.isArray(levelContent?.questions)
    ? levelContent.questions
    : [];

  questions.forEach((question, questionIndex) => {
    errors.push(
      ...validateQuestion(question, {
        unitIndex: unitOrder - 1 ?? 0,
        unitName: unitName.trim() ?? "",
        levelIndex: levelContent?.order ?? 0,
        levelName,
        questionIndex,
      }),
    );
  });

  return errors;
};
